'use client';

import { createContext, ReactNode, useContext, useEffect } from 'react';
import { useUserInfo } from '@/provider/UserInfoProvider';
import {
  batchWatermarkRemovalEndpoint,
  aiHeadshotsEndpoint,
  batchRepairImageEndpoint,
  batchBackgroundRemoverEndpoint,
  aiExpandEndpoint,
  createSubscriptionEndpoint,
  unlockImageEndpoint,
} from '@/config/endpoints';

// 用户做了某些操作后，需要刷新用户信息的接口
const needRefreshEndpoint = [
  batchWatermarkRemovalEndpoint,
  aiHeadshotsEndpoint,
  batchRepairImageEndpoint,
  batchBackgroundRemoverEndpoint,
  aiExpandEndpoint,
  createSubscriptionEndpoint,
  unlockImageEndpoint,
];

// 定义拦截器类型
type Interceptor = {
  request?: (config: RequestInit) => RequestInit | Promise<RequestInit>;
  response?: (response: Response) => Response | Promise<Response>;
  error?: (error: any) => any;
};

// 创建一个自定义Hook来管理拦截器
function useFetchInterceptorManager() {
  const userInfo = useUserInfo();
  const interceptors: Interceptor[] = [];

  // 添加拦截器
  const use = (interceptor: Interceptor) => {
    interceptors.push(interceptor);
    return () => {
      const index = interceptors.indexOf(interceptor);
      if (index > -1) {
        interceptors.splice(index, 1);
      }
    };
  };

  // 处理请求拦截
  const handleRequest = async (config: RequestInit): Promise<RequestInit> => {
    let finalConfig = { ...config };
    for (const interceptor of interceptors) {
      if (interceptor.request) {
        finalConfig = await interceptor.request(finalConfig);
      }
    }
    return finalConfig;
  };

  // 处理响应拦截
  const handleResponse = async (response: Response): Promise<Response> => {
    let finalResponse = response;
    for (const interceptor of interceptors) {
      if (interceptor.response) {
        finalResponse = await interceptor.response(finalResponse);
      }
    }
    return finalResponse;
  };

  // 处理错误拦截
  const handleError = async (error: any): Promise<any> => {
    let finalError = error;
    for (const interceptor of interceptors) {
      if (interceptor.error) {
        finalError = await interceptor.error(finalError);
      }
    }
    return finalError;
  };

  // 创建拦截后的fetch函数
  const createFetch = () => {
    const originalFetch = window.fetch;

    window.fetch = async function (
      input: RequestInfo | URL,
      init?: RequestInit
    ) {
      try {
        // 处理请求拦截
        const config = await handleRequest(init || {});

        // 发起请求
        const response = await originalFetch(input, config);

        // 处理响应拦截
        const interceptedResponse = await handleResponse(response);

        return interceptedResponse;
      } catch (error) {
        // 处理错误拦截
        throw await handleError(error);
      }
    };
  };

  // 添加默认的拦截器
  use({
    request: async (config) => {
      return config;
    },
    response: async (response) => {
      needRefreshEndpoint.includes(response.url) && userInfo.updateUserInfo();
      return response;
    },
    error: async (error) => {
      return error;
    },
  });

  // 在组件挂载时初始化拦截器
  useEffect(() => {
    createFetch();
    // 这里不需要依赖，因为createFetch只会在组件挂载时初始化一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    use,
  };
}

type FetchInterceptorContextType = {
  use: (interceptor: Interceptor) => () => void;
};

const FetchInterceptorContext = createContext<FetchInterceptorContextType>({
  use: () => () => {},
});

export function FetchInterceptorProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { use } = useFetchInterceptorManager();
  return (
    <FetchInterceptorContext.Provider value={{ use }}>
      {children}
    </FetchInterceptorContext.Provider>
  );
}

export function useFetchInterceptor() {
  return useContext(FetchInterceptorContext);
}

export default FetchInterceptorProvider;
